(function () {
  'use strict';

  var ROOT_SELECTOR = '.sh-studio';
  var GROUP_SELECTOR = '[data-sh-option]';
  var INPUT_SELECTOR = 'input[data-variant-input]';
  var TRACK_SELECTOR = '[data-sh-aa-option-track]';
  var TOGGLE_SELECTOR = '[data-sh-aa-toggle]';

  function all(scope, selector) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function optionPosition(inputOrGroup) {
    var raw = inputOrGroup && (
      inputOrGroup.getAttribute('data-option-position') ||
      inputOrGroup.getAttribute('data-option-index') ||
      inputOrGroup.getAttribute('data-index')
    );
    var match = String(raw || '').match(/\d+/);
    return match ? Number(match[0]) : 0;
  }

  function variantData(root) {
    var node = root.querySelector('[data-sh-variant-meta]');
    if (!node) return [];
    try {
      var parsed = JSON.parse(node.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function selectedByPosition(root) {
    var selected = {};
    all(root, INPUT_SELECTOR + ':checked').forEach(function (input) {
      var position = optionPosition(input);
      if (position) selected[position] = input.value;
    });
    return selected;
  }

  /*
    Availability is evaluated in Shopify option order. A candidate uses the
    selections before it, never the selections after it. That prevents the
    deadlock where changing option one would otherwise require changing option
    two first, while still making impossible downstream combinations explicit.
  */
  function candidateState(variants, selected, position, value) {
    var matches = variants.filter(function (variant) {
      if (!variant.options || String(variant.options[position - 1]) !== String(value)) return false;
      for (var index = 1; index < position; index += 1) {
        if (selected[index] != null && String(variant.options[index - 1]) !== String(selected[index])) {
          return false;
        }
      }
      return true;
    });

    return {
      exists: matches.length > 0,
      available: matches.some(function (variant) { return variant.available === true; })
    };
  }

  function applyCardState(wrapper, input, state) {
    var checked = input.checked;
    var nextState;
    var status = '';

    if (checked && state.available) {
      nextState = 'selected';
      status = 'Selected';
    } else if (checked) {
      nextState = 'selected-sold-out';
      status = state.exists ? 'Sold out' : 'Unavailable';
    } else if (state.available) {
      nextState = 'available';
      status = 'Available';
    } else if (state.exists) {
      nextState = 'sold-out';
      status = 'Sold out';
    } else {
      nextState = 'unavailable';
      status = 'Unavailable';
    }

    wrapper.setAttribute('data-sh-state', nextState);
    wrapper.classList.remove('is-gone');
    input.disabled = !checked && !state.available;

    if (!state.available) input.setAttribute('aria-disabled', 'true');
    else input.removeAttribute('aria-disabled');

    var label = input.nextElementSibling;
    if (label && !label.matches('label')) label = null;
    if (label) {
      if (!state.available) label.setAttribute('aria-disabled', 'true');
      else label.removeAttribute('aria-disabled');
    }

    var bar = wrapper.querySelector('[data-sh-option-status-bar]');
    if (bar) bar.textContent = status;
  }

  function rankGroupChoices(group) {
    var track = group.hasAttribute('data-sh-aa-option-track');
    var available = [];
    var unavailable = [];

    all(group, '[data-sh-option-value]').forEach(function (wrapper) {
      var state = wrapper.getAttribute('data-sh-state');
      if (state === 'sold-out' || state === 'unavailable' || state === 'selected-sold-out') {
        unavailable.push(wrapper);
      } else {
        available.push(wrapper);
      }
    });

    available.concat(unavailable).forEach(function (wrapper, index) {
      var rank = index + 1;
      wrapper.style.setProperty('--sh-aa-order', String(rank));
      wrapper.setAttribute('data-sh-sort-rank', String(rank));
      if (track && rank > 6) wrapper.setAttribute('data-sh-aa-overflow-choice', '');
      else wrapper.removeAttribute('data-sh-aa-overflow-choice');
    });
  }

  function updateGroup(root, group, variants, selected) {
    var position = optionPosition(group);
    all(group, INPUT_SELECTOR).forEach(function (input) {
      var wrapper = input.closest('[data-sh-option-value]');
      if (!wrapper || !position) return;
      applyCardState(wrapper, input, candidateState(variants, selected, position, input.value));
    });
    rankGroupChoices(group);
  }

  function updateLabels(root) {
    all(root, GROUP_SELECTOR).forEach(function (group) {
      var checked = group.querySelector(INPUT_SELECTOR + ':checked');
      var label = group.querySelector('[data-sh-selected-value]');
      if (checked && label) {
        var customerLabel = checked.getAttribute('data-sh-customer-label') || checked.value;
        var customerDetail = checked.getAttribute('data-sh-customer-detail') || '';
        var current = customerLabel + (customerDetail ? ' · ' + customerDetail : '');
        label.textContent = current;
        label.setAttribute('title', current);
      }
    });
  }

  function selectedVariant(variants, selected) {
    return variants.find(function (variant) {
      if (!variant.options) return false;
      for (var index = 1; index <= variant.options.length; index += 1) {
        if (selected[index] == null || String(variant.options[index - 1]) !== String(selected[index])) {
          return false;
        }
      }
      return true;
    }) || null;
  }

  function updateSelectionSummary(root, variants, selected) {
    var summary = root.querySelector('[data-sh-selection-summary]');
    if (!summary) return;

    var labels = all(root, INPUT_SELECTOR + ':checked:not([data-sh-singleton="true"])')
      .sort(function (a, b) { return optionPosition(a) - optionPosition(b); })
      .map(function (input) {
        return input.getAttribute('data-sh-customer-label') || input.value;
      })
      .filter(Boolean);

    var copy = summary.querySelector('[data-sh-selection-copy]');
    if (copy) copy.textContent = labels.join(' · ');

    var variant = selectedVariant(variants, selected);
    var price = summary.querySelector('[data-sh-selection-price]');
    if (price && variant) price.textContent = formatMoney(root, variant.price);
  }

  function formatMoney(root, cents) {
    var format = root.getAttribute('data-money-format') || '${{amount}}';
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, format);
    }
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, format);
    }
    var currency = window.Shopify && Shopify.currency && Shopify.currency.active
      ? Shopify.currency.active
      : 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return (Number(cents || 0) / 100).toFixed(2);
    }
  }

  /*
    Upgrade copy is relative to the least expensive compatible value in the
    same option. The selected value is not treated as the baseline, so a
    selected premium size still says +£x. Zero-cost values stay intentionally
    blank. Baseline cost is already communicated by the product price and the
    selected outline; only a genuine upgrade should add price copy.
  */
  function candidatePrice(variants, selected, position, value) {
    var exact = variants.filter(function (variant) {
      if (!variant.available || !variant.options) return false;
      if (String(variant.options[position - 1]) !== String(value)) return false;
      for (var index = 1; index <= variant.options.length; index += 1) {
        if (index === position) continue;
        if (selected[index] != null && String(variant.options[index - 1]) !== String(selected[index])) {
          return false;
        }
      }
      return true;
    });

    var matches = exact;
    if (!matches.length) {
      matches = variants.filter(function (variant) {
        if (!variant.available || !variant.options) return false;
        if (String(variant.options[position - 1]) !== String(value)) return false;
        for (var index = 1; index < position; index += 1) {
          if (selected[index] != null && String(variant.options[index - 1]) !== String(selected[index])) {
            return false;
          }
        }
        return true;
      });
    }

    if (!matches.length) return null;
    return matches.reduce(function (lowest, variant) {
      var price = Number(variant.price);
      return Number.isFinite(price) && (lowest == null || price < lowest) ? price : lowest;
    }, null);
  }

  function updatePriceDeltas(root, variants, selected) {
    all(root, GROUP_SELECTOR).forEach(function (group) {
      var priceNodes = all(group, '[data-sh-price-copy]');
      var position = optionPosition(group);
      if (!priceNodes.length || !position) return;

      var entries = priceNodes.map(function (node) {
        var wrapper = node.closest('[data-sh-option-value]');
        var input = wrapper && wrapper.querySelector(INPUT_SELECTOR);
        return {
          node: node,
          price: input ? candidatePrice(variants, selected, position, input.value) : null
        };
      });

      var baseline = entries.reduce(function (lowest, entry) {
        return entry.price != null && (lowest == null || entry.price < lowest) ? entry.price : lowest;
      }, null);

      entries.forEach(function (entry) {
        var delta = baseline != null && entry.price != null ? entry.price - baseline : 0;
        if (delta > 0) {
          entry.node.textContent = '+' + formatMoney(root, delta);
          entry.node.hidden = false;
        } else {
          entry.node.textContent = '';
          entry.node.hidden = true;
        }
      });
    });
  }

  function sync(root) {
    var variants = variantData(root);
    if (!variants.length) return;
    var selected = selectedByPosition(root);

    all(root, GROUP_SELECTOR)
      .sort(function (a, b) { return optionPosition(a) - optionPosition(b); })
      .forEach(function (group) {
        updateGroup(root, group, variants, selected);
      });

    updateLabels(root);
    updatePriceDeltas(root, variants, selected);
    updateSelectionSummary(root, variants, selected);
  }

  function updateRangeControl(group) {
    if (!group) return;
    var track = group.querySelector(TRACK_SELECTOR);
    var toggle = group.querySelector(TOGGLE_SELECTOR);
    if (!track || !toggle) return;

    var expanded = track.classList.contains('is-expanded');
    var count = Number(track.getAttribute('data-option-count')) || all(track, '[data-sh-option-value]').length;
    var copy = toggle.querySelector('[data-sh-aa-toggle-copy]');
    toggle.setAttribute('aria-expanded', String(expanded));
    if (copy) copy.textContent = expanded ? 'Show less' : 'View all ' + count;
  }

  function toggleRange(button) {
    var group = button && button.closest(GROUP_SELECTOR);
    var track = group && group.querySelector(TRACK_SELECTOR);
    if (!track) return;
    track.classList.toggle('is-expanded');
    updateRangeControl(group);
  }

  function repairFollowingSelections(root, changedPosition) {
    var variants = variantData(root);
    var groups = all(root, GROUP_SELECTOR)
      .sort(function (a, b) { return optionPosition(a) - optionPosition(b); });

    groups.forEach(function (group) {
      var position = optionPosition(group);
      if (!position || position <= changedPosition) return;

      var selected = selectedByPosition(root);
      updateGroup(root, group, variants, selected);

      var checked = group.querySelector(INPUT_SELECTOR + ':checked');
      var checkedWrapper = checked && checked.closest('[data-sh-option-value]');
      var invalid = checkedWrapper && (
        checkedWrapper.getAttribute('data-sh-state') === 'selected-sold-out'
      );

      if (!invalid) return;

      var replacement = all(group, INPUT_SELECTOR).find(function (input) {
        return !input.disabled && !input.checked;
      });

      if (replacement) {
        replacement.checked = true;
        updateGroup(root, group, variants, selectedByPosition(root));
        replacement.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    sync(root);
  }

  function initRoot(root) {
    if (!root || root.getAttribute('data-sh-adaptive-atelier-ready') === 'true') return;

    root.setAttribute('data-sh-adaptive-atelier-ready', 'true');

    root.addEventListener('change', function (event) {
      if (!event.target.matches(INPUT_SELECTOR)) return;
      repairFollowingSelections(root, optionPosition(event.target));

      // Keep the disclosure open while customers compare and select plugs.
    });

    root.addEventListener('click', function (event) {
      var button = event.target.closest(TOGGLE_SELECTOR);
      if (!button || !root.contains(button)) return;
      toggleRange(button);
    });

    root.addEventListener('variantChange', function () {
      window.setTimeout(function () { sync(root); }, 0);
    });
  }

  function boot(scope) {
    var roots = [];
    if (scope && scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    roots = roots.concat(all(scope || document, ROOT_SELECTOR));
    roots.forEach(initRoot);
  }


  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target || document);
  });

  window.SickHausAdaptiveAtelier = {
    sync: sync,
    repairFollowingSelections: repairFollowingSelections,
    toggleRange: toggleRange
  };
})();
